import { Namespace, useT } from "@racoon/i18n";
import { IcChevron } from "@racoon/icons";
import {
  Button,
  CardTabs,
  shouldShowCardFooter,
  Modal,
  ModalContent,
  ModalTrigger,
  ModalTitle,
  useToast,
  Separator,
  cn,
} from "@racoon/ui";
import * as React from "react";
import { useForm } from "react-hook-form";
import { InformationBlock } from "../components/information.block";
import { MAP_DEFAULT_CENTER, parseTwoGisCoords } from "@racoon/lib";
import { ContactsBlock } from "../components/contacts.block";
import { MapWithGeofence } from "~/features/map-view/ui/map-view";
import { GeofenceEditor } from "~/shared/ui/geofence-editor/geofence-editor";
import {
  useCreatePickupLocationMutation,
  useUpdatePickupLocationMutation,
  useListPickupLocationPhotosQuery,
} from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useCreateContactMutation } from "~/shared/api/contact/contact.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import { LocationStatus, ConnectProto } from "@racoon/api";
import { isNumericId } from "@racoon/lib";
import { useEffect, useRef, useState } from "react";
import { useModalKeyboardNavigation } from "~/shared/hooks/useModalKeyboardNavigation";
import { LocationPhotosSlider } from "../components/location-photos-slider";
import { PhotosBlock } from "../components/photos.block";
import { flattenLocationPhotos } from "../model/location-photo";
import { ContainerGroupsBlock } from "~/features/pickup-location-modal/components/container-groups.block";
import { ParticipantsBlock } from "~/features/pickup-location-modal/components/participants.block";
import { ScheduleBlock } from "~/features/pickup-location-modal/components/schedule.block";
import { BlacklistBlock } from "~/features/pickup-location-modal/components/blacklist.block";
import { useBlacklistDraft } from "~/features/pickup-location-modal/hooks/use-blacklist-draft";
import {
  buildGrpcGeometry,
  generateCircleGeofence,
  withoutReplacedSuggestion,
  type Geofence,
} from "~/shared/lib/geofence";
import { AROUND_THE_CLOCK_SCHEDULE } from "~/shared/lib/availability-schedule";
export type Props = {
  location?: (Omit<PickupLocationForm, "id"> & { id?: number }) | undefined;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  setLocation?: (location: PickupLocationForm | null) => void;
  hideTrigger?: boolean;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  /** Another organization's site, e.g. a zone job's: shown, not edited. */
  readOnly?: boolean;
};

/** Basic shape of our form. Extend as needed. */
export type PickupLocationForm = {
  id?: string | number;
  name: string;
  /** The provider's pickup location type id, "0" for none («Не указано»). */
  pickupLocationTypeId: string;
  /** The type as stored with the site: another provider's is on no type list of ours. */
  pickupLocationType?: ConnectProto.OrganizationLabel;
  /** Legal address of the site — proto `address`. */
  address: string;
  twoGisUrl: string;
  is24h: boolean;
  workFrom?: string;
  workTo?: string;
  geozone: string | number;
  tariff: number;
  priority: "low" | "medium" | "high";
  bunkers: number;
  geofence: Geofence[];
  status?: LocationStatus;
  primary_contact_id?: number;
  // contracts tab fields:
  clientId?: string;
  clientBin?: string;
  contractTerm?: string; // e.g. "12 months"
  activeContractId?: string | null; // null => “No contract”
  contact_phone: string;
  contact_name: string;
  latitude: string;
  longitude: string;
};

const defaultValues: PickupLocationForm = {
  name: "",
  pickupLocationTypeId: "0",
  address: "",
  twoGisUrl: "",
  is24h: true,
  workFrom: "09:00",
  workTo: "20:00",
  geozone: "1",
  tariff: 4500,
  latitude: "",
  longitude: "",

  priority: "medium",
  bunkers: 1,
  geofence: [],
  clientId: "",
  clientBin: "",
  contractTerm: "",
  activeContractId: null,
  contact_name: "",
  contact_phone: "",
  primary_contact_id: undefined,
};

export const PickupLocationModal = ({
  location,
  open,
  onOpenChange,
  setLocation,
  hideTrigger,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  readOnly,
}: Props) => {
  const { toast } = useToast();

  const t = useT();

  const isEdit = !!(location && location.id);

  /**
   * Single source of truth for this card's sections. Both the sidebar and the
   * keyboard navigation derive from it, so a tab can never be reachable by one
   * and invisible to the other.
   */
  const tabItems = React.useMemo(() => {
    const items = [
      { key: "info", label: t(Namespace.PickupLocation, "information") },
      { key: "participants", label: t(Namespace.PickupLocation, "participants.title") },
      { key: "container-groups", label: t(Namespace.PickupLocation, "containerGroups") },
      { key: "contacts", label: t(Namespace.PickupLocation, "contacts") },
      { key: "photos", label: t(Namespace.PickupLocation, "photos") },
      { key: "blacklist", label: t(Namespace.PickupLocation, "blacklist.title") },
      { key: "schedule", label: t(Namespace.PickupLocation, "schedule.title") },
    ];
    // Every section handles the no-id state itself (contacts stage and are
    // flushed after create; the rest show their empty/disabled hints) — except
    // the blacklist: its draft flushes by location id and the create flow never
    // flushes it, so rows staged before the location exists would be dropped
    // silently on save. That tab appears only once the location is real.
    // A foreign site shows only what needs no editor of its own
    if (readOnly) return items.filter((item) => item.key === "info" || item.key === "photos");
    return isEdit ? items : items.filter((item) => item.key !== "blacklist");
  }, [t, isEdit, readOnly]);

  const tabKeys = React.useMemo(() => tabItems.map((item) => item.key), [tabItems]);

  const { activeTab, setActiveTab } = useModalKeyboardNavigation({
    isOpen: !!open,
    tabs: tabKeys,
    initialTab: "info",
    hasPrevious,
    hasNext,
    onPrevious,
    onNext,
  });
  const [scheduleTabOpened, setScheduleTabOpened] = useState(false);
  useEffect(() => {
    if (activeTab === "schedule") setScheduleTabOpened(true);
  }, [activeTab]);

  const form = useForm<PickupLocationForm>({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (open) {
      if (location) {
        form.reset(location);
      } else {
        form.reset(defaultValues);
      }
    }
  }, [location, open, form]);

  const { selectedCompanyId } = useSelectedCompany();
  const [pendingContacts, setPendingContacts] = useState<ConnectProto.Contact[]>([]);
  const [createContact] = useCreateContactMutation();

  const [createPickupLocation] = useCreatePickupLocationMutation();
  const [updatePickupLocation] = useUpdatePickupLocationMutation();

  const { data: photoList, isLoading: isPhotosLoading } = useListPickupLocationPhotosQuery(
    { pickupLocationId: BigInt(location?.id ?? 0) },
    { skip: !location?.id },
  );
  const photos = React.useMemo(() => flattenLocationPhotos(photoList), [photoList]);

  // currentData/isFetching rather than data/isLoading: RTK Query keeps the
  // previous location's result in `data` while it refetches for a new one, and
  // `isLoading` is true only for the very first request. Reading the retained
  // value would show — and save — the groups of whichever location the modal
  // was on before this one. `currentData` is undefined until this location's
  // own response lands.
  const { currentData: cgResponse, isFetching: isCgFetching } = useListContainerGroupsQuery(
    { pickupLocationIds: location?.id ? [BigInt(location.id)] : [] },
    { skip: !location?.id },
  );
  const containerGroups = cgResponse?.containerGroups || [];

  // Saving a contact must wait for this location's own groups. An empty list is
  // a meaningful value — a contact of the customer as a whole — so submitting
  // one that is merely not loaded yet silently changes what the contact means
  // rather than doing nothing. Skipped queries (a location that does not exist
  // yet) have no groups to wait for, hence the location?.id check.
  const areGroupsUnresolved = Boolean(location?.id) && (isCgFetching || !cgResponse);

  // Blacklist edits are staged here and applied by onSubmit, so that Save is what
  // changes the list — nothing is written while the dispatcher is still editing.
  const blacklistDraft = useBlacklistDraft({
    pickupLocationId: location?.id,
    serviceProviderId: selectedCompanyId ? Number(selectedCompanyId) : undefined,
  });

  // The blacklist is written from the Save button itself rather than only from
  // the form's submit handler. react-hook-form refuses to submit while ANY
  // field is invalid — including fields this tab has nothing to do with — and a
  // dispatcher who staged blacklist changes and pressed Save must not have them
  // silently dropped because of an unrelated field. flush() is guarded against
  // running twice, so the submit handler calling it as well is harmless.
  const flushBlacklist = React.useCallback(async () => {
    try {
      await blacklistDraft.flush();
    } catch (error) {
      console.error("Failed to save blacklist changes", error);
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "blacklist.saveFailed"),
      });
    }
  }, [blacklistDraft, toast, t]);

  const onRestore = React.useCallback(async () => {
    if (isEdit && location?.id) {
      await updatePickupLocation({
        id: BigInt(location.id),
        serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
        ownerOrganizationId: location.clientId ? BigInt(location.clientId) : undefined,
        status: ConnectProto.LocationStatus.ACTIVE,
      }).unwrap();
      toast({
        title: t(Namespace.PickupLocation, "restored"),
      });
      form.reset(defaultValues);
      onOpenChange?.(false);
    } else {
      form.reset(defaultValues);
      onOpenChange?.(false);
    }
  }, [form, isEdit, location, selectedCompanyId, updatePickupLocation, t, onOpenChange]);

  // ---- ADDED VALIDATION FOR LAT/LON ----
  const validateLatLon = (lat: string, lon: string) => {
    // Should be non-empty and numeric and within valid ranges
    if (!lat || !lon) return false;
    const nLat = Number(lat);
    const nLon = Number(lon);
    if (isNaN(nLat) || isNaN(nLon)) return false;
    // Typical latitude [-90,90], longitude [-180,180]
    if (nLat < -90 || nLat > 90) return false;
    if (nLon < -180 || nLon > 180) return false;
    return true;
  };

  const priorityMap: Record<string, ConnectProto.PickupLocationPriority> = {
    high: ConnectProto.PickupLocationPriority.HIGH,
    medium: ConnectProto.PickupLocationPriority.MEDIUM,
    low: ConnectProto.PickupLocationPriority.LOW,
  };

  const onInvalid = React.useCallback(
    (errors: Record<string, { message?: unknown } | undefined>) => {
      const firstMessage = Object.values(errors)
        .map((error) => error?.message)
        .find((message): message is string => typeof message === "string" && message.length > 0);
      if (firstMessage) {
        toast({ variant: "destructive", title: firstMessage });
      }
    },
    [toast],
  );

  const onSubmit = form.handleSubmit(async (values) => {
    // Validate lat & lon - this allows sending ONLY if lat/lon are valid, as requested
    if (!validateLatLon(values.latitude, values.longitude)) {
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "invalidLatLon"),
        description: t(Namespace.PickupLocation, "invalidLatLonDescription"),
      });
      return; // Block sending if invalid
    }

    // Trim name if it exists
    if (values.name) {
      values.name = values.name.trim();
    }

    const availabilitySchedule =
      !values.is24h && values.workFrom && values.workTo
        ? `${values.workFrom} - ${values.workTo}`
        : AROUND_THE_CLOCK_SCHEDULE;

    // 0n clears the type on update; the deprecated enum `type` is left as stored.
    const pickupLocationTypeId = isNumericId(values.pickupLocationTypeId)
      ? BigInt(values.pickupLocationTypeId)
      : 0n;

    const grpcPriority = priorityMap[values.priority] ?? ConnectProto.PickupLocationPriority.MEDIUM;

    if (isEdit && location?.id) {
      await updatePickupLocation({
        id: BigInt(location.id),
        name: values.name,
        ownerOrganizationId: values.clientId ? BigInt(values.clientId) : undefined,
        serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
        pickupLocationTypeId,
        status:
          values.status && values.status !== location?.status
            ? values.status === LocationStatus.LOCATION_STATUS_ACTIVE
              ? ConnectProto.LocationStatus.ACTIVE
              : ConnectProto.LocationStatus.INACTIVE
            : undefined,
        lon: Number(values.longitude),
        lat: Number(values.latitude),
        geometry: buildGrpcGeometry(values.geofence),
        availabilitySchedule,
        notificationSchedule: "1d",
        numContainers: BigInt(values.bunkers || 0),
        priority: grpcPriority,
        geoGroup: String(values.geozone || ""),
        tariff: BigInt(values.tariff || 0),
        contractId: values.activeContractId ? BigInt(values.activeContractId) : undefined,
        gisLink: values.twoGisUrl || "",
        // Sent even when empty: `address` has explicit presence, so "" clears the
        // stored value while `undefined` would silently keep the old one.
        address: values.address?.trim() ?? "",
        primaryContactId: values.primary_contact_id ? BigInt(values.primary_contact_id) : undefined,
      }).unwrap();
      await flushBlacklist();
      toast({
        title: t(Namespace.PickupLocation, "updated"),
      });
      // Saving an existing location keeps the card open — the dispatcher is
      // usually part-way through several of its sections. Reset to the values
      // just submitted rather than to `defaultValues`: it clears the form's
      // dirty state without throwing away what was saved while the refetched
      // location makes its way back.
      form.reset(values);
    } else {
      const res = await createPickupLocation({
        name: values.name,
        ownerOrganizationId: values.clientId ? BigInt(values.clientId) : undefined,
        serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
        // Still required by the proto until the enum is dropped.
        type: ConnectProto.PickupLocationType.UNDEFINED,
        pickupLocationTypeId: pickupLocationTypeId || undefined,
        lon: Number(values.longitude),
        lat: Number(values.latitude),
        geometry: buildGrpcGeometry(values.geofence),
        availabilitySchedule,
        notificationSchedule: "1d",
        numContainers: BigInt(values.bunkers || 0),
        priority: grpcPriority,
        geoGroup: String(values.geozone || ""),
        tariff: BigInt(values.tariff || 0),
        contractId: values.activeContractId ? BigInt(values.activeContractId) : undefined,
        gisLink: values.twoGisUrl || "",
        address: values.address?.trim() ?? "",
      }).unwrap();

      const newLocationId = Number(res.id);

      if (pendingContacts.length > 0 && newLocationId) {
        try {
          await Promise.all(
            pendingContacts.map((contact) => {
              // `clientId` is the only source for the owning org: every caller builds
              // `location` via formatPickupLocationToForm, which sets `clientId` and
              // never a snake_case `owner_organization_id`.
              const orgId = values.clientId ? Number(values.clientId) : undefined;
              const isValidOrgId = typeof orgId === "number" && !isNaN(orgId) && orgId > 0;

              return createContact({
                name: contact.name,
                phone: contact.phone,
                email: contact.email,
                position: contact.position,
                // A location being created has no container groups yet, so
                // these start out belonging to the customer as a whole.
                ownerOrganizationId: BigInt(isValidOrgId ? orgId : 0),
              }).unwrap();
            }),
          );
        } catch (e) {
          console.error("Failed to create pending contacts", e);
          toast({
            variant: "destructive",
            title: t(Namespace.PickupLocation, "errorCreatingContactsTitle"),
            description: t(Namespace.PickupLocation, "errorCreatingContactsDescription"),
          });
        }
      }

      toast({
        title: t(Namespace.PickupLocation, "created"),
      });
      // Clear geofence after create
      form.setValue("geofence", []);
      setPendingContacts([]);
      form.reset(defaultValues);
      onOpenChange?.(false);
    }
  }, onInvalid);

  // Archive handler
  const onArchive = async () => {
    if (isEdit && location?.id) {
      await updatePickupLocation({
        id: BigInt(location.id),
        serviceProviderId: selectedCompanyId ? BigInt(selectedCompanyId) : undefined,
        ownerOrganizationId: location.clientId ? BigInt(location.clientId) : undefined,
        status: ConnectProto.LocationStatus.INACTIVE,
      }).unwrap();
      toast({
        title: t(Namespace.PickupLocation, "archived"),
      });
      form.reset(defaultValues);
      onOpenChange?.(false);
    }
  };

  // --- Custom logic for map center/zoom/geofence reset on 2GIS url change ---
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    form.watch("latitude") ? Number(form.watch("latitude")) : MAP_DEFAULT_CENTER.lat,
    form.watch("longitude") ? Number(form.watch("longitude")) : MAP_DEFAULT_CENTER.lng,
  ]);
  const [mapZoom, setMapZoom] = useState<number>(15);
  const [photosSliderOpen, setPhotosSliderOpen] = useState(false);
  const [initialPhotoSlide, setInitialPhotoSlide] = useState(0);

  // Keep previous url to detect change
  const prevTwoGisUrl = useRef<string | undefined>(undefined);

  // Watch for twoGisUrl changes
  const twoGisUrl = form.watch("twoGisUrl");
  // The site's pin: the 2GIS link's point, until it is placed or dragged on the map.
  const latitude = form.watch("latitude");
  const longitude = form.watch("longitude");
  const pin: [number, number] | null = validateLatLon(latitude, longitude)
    ? [Number(latitude), Number(longitude)]
    : null;

  // Effect: center map when location data is loaded
  useEffect(() => {
    if (location && location.latitude && location.longitude) {
      const lat = Number(location.latitude);
      const lon = Number(location.longitude);
      if (!isNaN(lat) && !isNaN(lon)) {
        setMapCenter([lat, lon]);
        // Also force zoom to 15 when switching between existing locations
        setMapZoom((prev) => (prev === 15 ? 15.000001 : 15));
      }
    }
  }, [location]);

  // Effect: when twoGisUrl changes, center/zoom map and clear geofences
  useEffect(() => {
    if (twoGisUrl && twoGisUrl !== prevTwoGisUrl.current) {
      const coords = parseTwoGisCoords(twoGisUrl);
      if (coords) {
        setMapCenter([coords.lat, coords.lon]);

        // Don't clear geofence & lat/lon if this change is just from the initial form.reset()
        const isInitialLoad = location && location.twoGisUrl === twoGisUrl;
        if (!isInitialLoad) {
          setMapZoom(15); // or any zoom you want
          form.setValue("latitude", coords.lat.toString());
          form.setValue("longitude", coords.lon.toString());

          const currentGeofence = form.getValues("geofence");
          if (!currentGeofence || currentGeofence.length === 0) {
            const circleCoords = generateCircleGeofence(coords.lat, coords.lon, 50);
            const geofenceId =
              typeof globalThis !== "undefined" &&
              typeof globalThis.crypto !== "undefined" &&
              typeof globalThis.crypto.randomUUID === "function"
                ? globalThis.crypto.randomUUID()
                : Date.now().toString();
            form.setValue("geofence", [{ id: `auto-${geofenceId}`, latlngs: circleCoords }]);
          }
        }
      }
    }
    prevTwoGisUrl.current = twoGisUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoGisUrl, location]);

  // `info` is the form itself, `contacts` stages the primary contact onto it,
  // and `blacklist` stages its own additions and removals. The blacklist draft
  // counts as dirty too — the form knows nothing about it.
  const showFooter = shouldShowCardFooter({
    isEdit,
    activeTab,
    footerTabs: ["info", "contacts", "blacklist"],
    isDirty: form.formState.isDirty || blacklistDraft.isDirty,
  });
  const showStatusActions = isEdit && activeTab === "info";

  /**
   * Every section stays mounted and is hidden with `display:none` when inactive,
   * so in-progress edits and scroll positions survive tab switches. The map is
   * the exception — as before, it is only rendered while `info` is active.
   */
  /**
   * The design places the map to the left of the fields, taking roughly 41% of the
   * content width rather than half.
   */
  const mapPanel = (
    <div className="w-full md:w-[41%] shrink-0 flex flex-col">
      <div className="rounded-lg border bg-muted/20 h-[340px] md:h-full md:min-h-[300px] overflow-hidden flex items-center justify-center relative">
        <MapWithGeofence
          hideFullscreenButton={true}
          center={mapCenter}
          zoom={mapZoom}
          className="w-full h-full"
        >
          <GeofenceEditor
            pin={pin}
            geofences={form.watch("geofence") ?? []}
            // Without onChange the editor only shows the pin and fences.
            onChange={
              readOnly
                ? undefined
                : (draft) => {
                    // A removed pin clears the point; saving then asks for one, as without a link.
                    form.setValue("latitude", draft.pin ? String(draft.pin[0]) : "", {
                      shouldDirty: true,
                    });
                    form.setValue("longitude", draft.pin ? String(draft.pin[1]) : "", {
                      shouldDirty: true,
                    });
                    form.setValue("geofence", withoutReplacedSuggestion(draft.geofences), {
                      shouldDirty: true,
                    });
                  }
            }
          />
        </MapWithGeofence>
      </div>
    </div>
  );

  const cardBody = (
    <div className="flex h-full w-full flex-col gap-4 md:flex-row">
      {activeTab === "info" && mapPanel}
      <div
        className={cn(
          "flex flex-col min-w-0",
          activeTab === "info" ? "w-full flex-1" : "w-full h-full",
        )}
      >
        <div className={cn("flex flex-1", activeTab !== "info" && "hidden")}>
          <fieldset disabled={readOnly} className="contents">
            <InformationBlock
              form={form}
              serviceProviderId={selectedCompanyId}
              storedType={location?.pickupLocationType}
            />
          </fieldset>
        </div>

        {/* A read-only card offers only info and photos: the editing tabs are not mounted. */}
        {!readOnly && (
          <>
            <div className={cn("h-full min-h-0", activeTab !== "participants" && "hidden")}>
              <ParticipantsBlock
                locationId={location?.id}
                containerGroups={containerGroups}
                isLoading={isCgFetching}
              />
            </div>

            <div className={cn("h-full min-h-0", activeTab !== "container-groups" && "hidden")}>
              <ContainerGroupsBlock
                locationId={location?.id}
                containerGroups={containerGroups}
                isLoading={isCgFetching}
              />
            </div>

            <div className={cn("h-full min-h-0", activeTab !== "contacts" && "hidden")}>
              <ContactsBlock
                locationId={location?.id}
                organizationId={form.watch("clientId") ? Number(form.watch("clientId")) : undefined}
                pendingContacts={pendingContacts}
                setPendingContacts={setPendingContacts}
                isCustomerCard={false}
                primaryContactId={form.watch("primary_contact_id")}
                onPrimaryChange={(id) => form.setValue("primary_contact_id", id)}
                containerGroups={containerGroups}
                isGroupsLoading={areGroupsUnresolved}
              />
            </div>
          </>
        )}

        <div className={cn("h-full min-h-0", activeTab !== "photos" && "hidden")}>
          <PhotosBlock
            photos={photos}
            isLoading={isPhotosLoading}
            onPhotoClick={(index) => {
              setInitialPhotoSlide(index);
              setPhotosSliderOpen(true);
            }}
          />
        </div>

        {!readOnly && (
          <div className={cn("h-full min-h-0", activeTab !== "blacklist" && "hidden")}>
            <BlacklistBlock
              rows={blacklistDraft.rows}
              isLoading={blacklistDraft.isLoading}
              serviceProviderId={selectedCompanyId ? Number(selectedCompanyId) : undefined}
              onAdd={blacklistDraft.addTruckType}
              onRemove={blacklistDraft.removeRow}
            />
          </div>
        )}

        {/* Mounted once first opened (it queries the location's schedules), then only hidden,
            so a tab switch keeps an open editor's draft; keyed so another location starts fresh. */}
        {scheduleTabOpened && !readOnly && (
          <div className={cn("h-full min-h-0", activeTab !== "schedule" && "hidden")}>
            <ScheduleBlock
              key={location?.id}
              locationId={location?.id}
              containerGroups={containerGroups}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <ModalTrigger asChild onClick={() => setLocation?.(null)}>
          <Button id="edit-pickup-location-trigger-button" variant="solidBlue" size={"sm"}>
            {t(Namespace.Buttons, "addLocation")}
          </Button>
        </ModalTrigger>
      )}

      <ModalContent
        size="custom"
        closeButtonTop="top-4"
        className="md:!w-[1200px] md:!h-[656px] overflow-hidden"
      >
        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="shrink-0 bg-white p-4 pr-12 flex flex-row items-center justify-start gap-4">
            <div className="flex flex-row max-h-8 items-center justify-start m-0 gap-2">
              {(onPrevious || onNext) && (
                <div className="flex items-center">
                  <Button
                    id="pickup-location-prev-button"
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 rounded-full"
                    disabled={!hasPrevious}
                    onClick={(e) => {
                      e.preventDefault();
                      onPrevious?.();
                    }}
                  >
                    <IcChevron className="rotate-180 h-5 w-5" />
                  </Button>
                  <Button
                    id="pickup-location-next-button"
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 rounded-full"
                    disabled={!hasNext}
                    onClick={(e) => {
                      e.preventDefault();
                      onNext?.();
                    }}
                  >
                    <IcChevron className="h-5 w-5" />
                  </Button>
                </div>
              )}
              <ModalTitle className="flex items-center m-0">
                <div className="flex flex-col gap-0.5">
                  <span className="text-base font-bold leading-tight text-text-primary">
                    {form.watch("name")}
                  </span>
                  <span className="text-[10px] font-medium uppercase leading-tight tracking-wide text-text-secondary">
                    {t(Namespace.Table, "object")}
                  </span>
                </div>
              </ModalTitle>
            </div>
          </div>
          <Separator />
          <CardTabs
            items={tabItems}
            value={activeTab}
            onValueChange={setActiveTab}
            idPrefix="pickup-location"
            panelClassName="p-4"
          >
            {cardBody}
          </CardTabs>
          {showFooter && !readOnly && (
            <>
              <Separator />
              <div className="shrink-0 p-4 flex justify-between items-center">
                <div className="flex gap-3 items-center">
                  {showStatusActions &&
                    form.watch("status") ===
                      ConnectProto.LocationStatusSchema.values[ConnectProto.LocationStatus.ACTIVE]
                        .name && (
                      <Button
                        id="edit-pickup-location-archive-button"
                        type="button"
                        variant="solidRed"
                        onClick={onArchive}
                      >
                        {t(Namespace.Buttons, "archive") ?? "Архивировать"}
                      </Button>
                    )}
                  {showStatusActions &&
                    form.watch("status") ===
                      ConnectProto.LocationStatusSchema.values[ConnectProto.LocationStatus.INACTIVE]
                        .name && (
                      <Button
                        id="edit-pickup-location-restore-button"
                        type="button"
                        variant="solidGreen"
                        onClick={onRestore}
                      >
                        {t(Namespace.Buttons, "restore") ?? "Восстановить"}
                      </Button>
                    )}
                </div>
                <Button
                  id="edit-pickup-location-submit-button"
                  type="submit"
                  variant="solidBlue"
                  onClick={() => void flushBlacklist()}
                >
                  {isEdit ? t(Namespace.Buttons, "save") : t(Namespace.Buttons, "add")}
                </Button>
              </div>
            </>
          )}
        </form>
      </ModalContent>

      {isEdit && location?.id && (
        <LocationPhotosSlider
          open={photosSliderOpen}
          onOpenChange={setPhotosSliderOpen}
          photos={photos}
          isLoading={isPhotosLoading}
          initialSlide={initialPhotoSlide}
          title={t(Namespace.PickupLocation, "locationPhotos" as any)}
        />
      )}
    </Modal>
  );
};
